import { useEffect, useState, useContext } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { axiosInstance } from "../../config";
import { AuthContext } from "../../context/AuthContext";
import Topbar from "../../components/topbar/Topbar";
import UpgradeModal from "../../components/UpgradeModal/UpgradeModal";
import { FiActivity, FiAlertTriangle, FiArchive, FiBarChart2, FiBriefcase, FiCheckCircle, FiCpu, FiCreditCard, FiDollarSign, FiFileText, FiFilm, FiGrid, FiInstagram, FiMessageSquare, FiRadio, FiScissors, FiSend, FiTarget, FiTrendingUp, FiUserPlus, FiUsers, FiVideo, FiZap } from "react-icons/fi";
import toast from "react-hot-toast";
import FounderPlaybook from "./FounderPlaybook";
import AffiliatesTab from "./AffiliatesTab";
import ClipReviewTab from "./ClipReviewTab";
import ClipQualityTab from "./ClipQualityTab";
import ClippersTab from "./ClippersTab";
import IgContentTab from "./IgContentTab";
import ContentEngineTab from "./ContentEngineTab";
import FunnelTab from "./FunnelTab";
import FinanceTab from "./FinanceTab";
import SmsTab from "./SmsTab";
import { AdminUsersList } from "../adminusers/AdminUsers";
import "./admindashboard.css";

// Remember which tab the admin was on (survives reloads). Keep VALID_TABS in sync with TABS below.
const TAB_KEY = "admindash.tab";
const VALID_TABS = ["overview", "analytics", "users", "health", "failed", "onboarding", "distribution", "affiliates", "clippers", "clip-review", "clip-quality", "instagram", "content-engine", "pnl"];
function loadTab() {
  try {
    // A ?tab= link (e.g. "Back to users" from a user's page) wins.
    // "funnel" was merged into "analytics" — old links/saved tabs land there.
    const norm = (t) => (t === "funnel" ? "analytics" : t);
    const fromUrl = norm(new URLSearchParams(window.location.search).get("tab"));
    if (VALID_TABS.includes(fromUrl)) return fromUrl;
    const t = norm(localStorage.getItem(TAB_KEY));
    return VALID_TABS.includes(t) ? t : "overview";
  } catch {
    return "overview";
  }
}

function formatRel(date) {
  if (!date) return "—";
  const ms = Date.now() - new Date(date).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function formatMoney(cents) {
  if (cents == null) return "—";
  return `$${(cents / 100).toFixed(2)}`;
}

// ISO alpha-2 country code → flag emoji (regional indicator letters).
function countryFlag(cc) {
  if (!cc || cc.length !== 2) return "";
  return String.fromCodePoint(
    ...cc.toUpperCase().split("").map((c) => 127397 + c.charCodeAt(0)),
  );
}

function formatLocation(geo) {
  if (!geo || !geo.country) return null;
  return [geo.city, geo.country].filter(Boolean).join(", ");
}

function LocationCell({ geo }) {
  const loc = formatLocation(geo);
  if (!loc) return <span className="muted">—</span>;
  return (
    <span title={[geo.city, geo.region, geo.country].filter(Boolean).join(", ")}>
      {countryFlag(geo.country)} {loc}
    </span>
  );
}

function dayLabel(isoDate) {
  const d = new Date(isoDate + "T00:00:00Z");
  return d.toLocaleDateString(undefined, { weekday: "short" });
}

// Founder motivational lines — rotates based on day. Hand-picked, not random
// every render, so the dashboard feels grounded instead of jokey.
const MOTIVATIONAL_LINES = [
  "Every clip generated is a creator you helped go viral.",
  "Ship something. Tell someone. Repeat.",
  "Your competitors are sleeping. You're shipping.",
  "Compound the smallest improvement, daily.",
  "The next 1,000 users are decided this week.",
  "Distribution > Product. Ship the distribution.",
  "One DM a day to a creator. That's the whole strategy.",
];

function todaysLine() {
  const day = Math.floor(Date.now() / 86400000);
  return MOTIVATIONAL_LINES[day % MOTIVATIONAL_LINES.length];
}

export default function AdminDashboard() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [health, setHealth] = useState(null);
  const [ga, setGa] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ttTestCode, setTtTestCode] = useState("");
  const [ttTesting, setTtTesting] = useState(false);
  const [tab, setTabState] = useState(loadTab);
  const [, setSearchParams] = useSearchParams();
  const setTab = (t) => {
    setTabState(t);
    try { localStorage.setItem(TAB_KEY, t); } catch { /* no-op */ }
    // Keep the URL in sync so refresh/back land on the same tab.
    setSearchParams({ tab: t }, { replace: true });
  };
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  async function sendTikTokTest() {
    setTtTesting(true);
    try {
      const res = await axiosInstance.post("/admin/analytics/tiktok-test", { testEventCode: ttTestCode.trim() || undefined });
      if (res.data?.ok) {
        toast.success(ttTestCode.trim() ? "Test event sent — check TikTok Test Events tab" : "CompletePayment sent to TikTok");
      } else if (res.data?.configured === false) {
        toast.error(res.data.message || "TikTok env not configured");
      } else {
        toast.error(res.data?.error || "TikTok rejected the event");
      }
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to send test event");
    } finally {
      setTtTesting(false);
    }
  }

  useEffect(() => { document.title = "Clipbait — Admin Dashboard"; }, []);

  useEffect(() => {
    if (!user) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  async function load() {
    setLoading(true);
    try {
      const [overview, healthRes, gaRes] = await Promise.all([
        axiosInstance.get("/admin/analytics/overview"),
        axiosInstance.get("/admin/analytics/health").catch(() => ({ data: null })),
        axiosInstance.get("/admin/analytics/ga").catch(() => ({ data: { configured: false } })),
      ]);
      setData(overview.data);
      setHealth(healthRes.data);
      setGa(gaRes.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error("Admin only");
        navigate("/");
        return;
      }
      toast.error(err.response?.data?.error || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }

  if (loading || !data) {
    return (
      <>
        <Topbar />
        <div className="admindash-page">
          <div className="admindash-container">
            <h1 className="admindash-title">Admin Dashboard</h1>
            <div className="admindash-empty admindash-shimmer">Loading…</div>
          </div>
        </div>
      </>
    );
  }

  const { totals, recentlyActive = [], recentSignups, weeklySignups, weeklyClips, referralSources, foundVia = [], revenue } = data;

  // Tabbed layout — toggle each section's visibility by active tab.
  const TABS = [
    { id: "overview", label: "Overview", icon: <FiGrid /> },
    { id: "analytics", label: "Analytics & Ads", icon: <FiBarChart2 /> },
    { id: "users", label: "Users", icon: <FiUsers /> },
    { id: "sms", label: "SMS", icon: <FiMessageSquare /> },
    { id: "health", label: "Health", icon: <FiActivity /> },
    { id: "failed", label: "Failed", icon: <FiAlertTriangle /> },
    { id: "onboarding", label: "Onboarding", icon: <FiFileText /> },
    { id: "distribution", label: "Distribution", icon: <FiSend /> },
    { id: "affiliates", label: "Affiliates", icon: <FiTarget /> },
    { id: "clippers", label: "Clippers", icon: <FiFilm /> },
    { id: "clip-review", label: "Clip Review", icon: <FiCheckCircle /> },
    { id: "clip-quality", label: "Clip Quality", icon: <FiScissors /> },
    { id: "instagram", label: "Instagram", icon: <FiInstagram /> },
    { id: "content-engine", label: "Content Engine", icon: <FiZap /> },
    { id: "pnl", label: "P&L", icon: <FiDollarSign /> },
  ];
  const show = (id) => ({ display: tab === id ? undefined : "none" });
  const maxSignup = Math.max(1, ...weeklySignups.map((d) => d.count));
  const maxClips = Math.max(1, ...(weeklyClips || []).map((d) => d.clips));

  return (
    <>
      <Topbar onUpgrade={() => setShowUpgradeModal(true)} />
      <UpgradeModal isOpen={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} currentPlan={totals?.plan || null} />
      <div className="admindash-page">
        <div className="admindash-container">
          <div className="admindash-header admindash-fade-in">
            <div>
              <h1 className="admindash-title">Founder dashboard</h1>
              <div className="admindash-motivation">{todaysLine()}</div>
            </div>
            <button className="admindash-refresh" onClick={load}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="23 4 23 10 17 10" />
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
              </svg>
              Refresh
            </button>
          </div>

          {/* Top tab navigation */}
          <div className="admindash-tabs admindash-fade-in">
            {TABS.map((tb) => (
              <button
                key={tb.id}
                className={`admindash-tab ${tab === tb.id ? "active" : ""}`}
                onClick={() => setTab(tb.id)}
              >
                <span className="admindash-tab-icon">{tb.icon}</span>
                {tb.label}
              </button>
            ))}
          </div>

          {/* Founder playbook — the trackable build-it-can't-fail strategy */}
          {tab === "overview" && <FounderPlaybook />}

          {/* Hero metrics — the numbers you check daily */}
          <div className="admindash-grid admindash-fade-in" style={show('overview')}>
            <Stat icon={<FiUsers />} label="Total users" value={totals.totalUsers} pulse />
            <Stat icon={<FiUserPlus />} label="New today" value={totals.newToday} accent="green" pulse={totals.newToday > 0} />
            <Stat icon={<FiTrendingUp />} label="New this week" value={totals.newThisWeek} accent="green" />
            <Stat icon={<FiCreditCard />} label="Paying users" value={totals.payingUsers} accent="purple" />
            <Stat
              icon={<FiDollarSign />}
              label="Revenue today"
              value={revenue.today ? formatMoney(revenue.today.totalCents) : revenue.stripeConfigured ? "$0.00" : "—"}
              accent="gold"
              pulse={revenue.today && revenue.today.totalCents > 0}
            />
            <Stat
              icon={<FiDollarSign />}
              label="Revenue this week"
              value={revenue.week ? formatMoney(revenue.week.totalCents) : revenue.stripeConfigured ? "$0.00" : "—"}
              accent="gold"
            />
            <Stat
              icon={<FiTrendingUp />}
              label={`MRR — live, app only${revenue.mrr?.activeCount != null ? ` (${revenue.mrr.activeCount} subs)` : ""}`}
              value={revenue.mrr ? formatMoney(revenue.mrr.activeCents) : revenue.stripeConfigured ? "$0.00" : "—"}
              accent="purple"
            />
          </div>
          {/* Real MRR straight from Stripe subscriptions — not Stripe Dashboard's own MRR tile, which
              shows whatever date range/mode happens to be selected there. Agency deals (Karazel etc.) are
              excluded from the app MRR above and called out here instead, so they're visible, not hidden. */}
          {revenue.mrr && (revenue.mrr.atRiskCents > 0 || revenue.mrr.agencyCents > 0) && (
            <div className="muted admindash-fade-in" style={{ ...show('overview'), fontSize: 12, marginTop: -6, marginBottom: 10 }}>
              {revenue.mrr.atRiskCents > 0 && <>{formatMoney(revenue.mrr.atRiskCents)}/mo at risk ({revenue.mrr.atRiskCount} past-due subscription{revenue.mrr.atRiskCount === 1 ? "" : "s"}). </>}
              {revenue.mrr.agencyCents > 0 && <>{formatMoney(revenue.mrr.agencyCents)}/mo excluded as agency revenue ({revenue.mrr.agencyCount} subscription{revenue.mrr.agencyCount === 1 ? "" : "s"}), not app MRR.</>}
            </div>
          )}

          {/* Output metrics — what creators are actually producing */}
          <div className="admindash-grid admindash-fade-in" style={show('overview')}>
            <Stat icon={<FiFilm />} label="Clips generated today" value={totals.clipsToday} accent="orange" pulse={totals.clipsToday > 0} />
            <Stat icon={<FiVideo />} label="Clips this week" value={totals.clipsThisWeek} accent="orange" />
            <Stat icon={<FiCpu />} label="Jobs today" value={totals.jobsToday} accent="blue" />
            <Stat icon={<FiArchive />} label="Jobs all-time" value={totals.jobsTotal} accent="blue" />
            <Stat icon={<FiScissors />} label="Clippers" value={totals.clippers} accent="cyan" />
            <Stat icon={<FiRadio />} label="Streamers" value={totals.streamers} accent="cyan" />
            <Stat icon={<FiBriefcase />} label="Clippers wanting paid opportunities" value={totals.paidClippers || 0} accent="gold" pulse={(totals.paidClippers || 0) > 0} />
          </div>

          {/* API health */}
          {health && (
            <div className="admindash-section admindash-fade-in" style={show('health')}>
              <h2>API Health</h2>
              <div className="admindash-health">
                <HealthCard
                  name="RunPod"
                  status={health.runpod}
                  detail={
                    health.runpod.ok ? (
                      <>
                        <div>
                          <b>${(health.runpod.clientBalance || 0).toFixed(2)}</b> balance
                        </div>
                        <div className="muted">${(health.runpod.currentSpendPerHr || 0).toFixed(4)}/hr current spend</div>
                        {health.runpod.spendLimit ? <div className="muted">${health.runpod.spendLimit} limit</div> : null}
                      </>
                    ) : null
                  }
                  configMissingMsg="Set RUNPOD_API_KEY env var"
                  dashboardUrl="https://www.console.runpod.io/user/billing"
                />
                <HealthCard
                  name="AssemblyAI"
                  status={health.assemblyai}
                  detail={<div className="muted">No live balance API — open dashboard to check credits</div>}
                  dashboardUrl={health.assemblyai.dashboardUrl}
                />
                <HealthCard
                  name="Gemini"
                  status={health.gemini}
                  detail={<div className="muted">No live balance API — open dashboard to check usage</div>}
                  dashboardUrl={health.gemini.dashboardUrl}
                />
              </div>
            </div>
          )}

          {/* ANALYTICS & ADS — Meta/TikTok conversion health, Get Clips → signup →
              paid, click geo, PostHog (was its own "Ads & Funnel" tab). */}
          {tab === "analytics" && <FunnelTab />}

          {/* Marketing site traffic — Google Analytics 4 (gtag G-YDNZBHZZPQ) */}
          <div className="admindash-section admindash-fade-in" style={show('analytics')}>
            <h2>
              Marketing site traffic
              {ga?.configured && (
                <span className="muted" style={{ fontSize: 13, fontWeight: 400, marginLeft: 10 }}>
                  last {ga.days} days
                  {typeof ga.activeNow === "number" && (
                    <span style={{ marginLeft: 12, color: "#34d399" }} title="Unique users active on the site in the last 30 minutes (GA4 realtime) — not concurrent viewers">
                      ● {ga.activeNow} active in last 30 min
                    </span>
                  )}
                </span>
              )}
            </h2>
            {ga?.configured ? (
              <>
                <div className="admindash-grid">
                  <Stat label="Visitors" value={ga.totals.activeUsers} pulse={ga.totals.activeUsers > 0} />
                  <Stat label="New visitors" value={ga.totals.newUsers} accent="green" />
                  <Stat label="Sessions" value={ga.totals.sessions} accent="blue" />
                  <Stat label="Page views" value={ga.totals.pageViews} accent="purple" />
                </div>
                {ga.byDay?.length > 0 && (() => {
                  const lbl = (s) => (s && s.length === 8 ? `${Number(s.slice(4, 6))}/${Number(s.slice(6, 8))}` : s);
                  const data = ga.byDay;
                  const n = data.length;
                  const max = Math.max(1, ...data.map((x) => x.activeUsers));
                  const peak = data.reduce((a, b) => (b.activeUsers > a.activeUsers ? b : a), data[0]);
                  const W = 1000, H = 160, pad = 6;
                  const xAt = (i) => (n <= 1 ? W / 2 : pad + (i / (n - 1)) * (W - pad * 2));
                  const yAt = (v) => H - pad - (v / max) * (H - pad * 2);
                  const line = data.map((d, i) => `${xAt(i)},${yAt(d.activeUsers)}`).join(" ");
                  const area = `${xAt(0)},${H} ${line} ${xAt(n - 1)},${H}`;
                  return (
                    <div style={{ marginTop: 18 }}>
                      <div className="muted" style={{ marginBottom: 8, display: "flex", justifyContent: "space-between" }}>
                        <span>Visitors per day · last {ga.days} days</span>
                        <span>peak {peak.activeUsers} on {lbl(peak.date)}</span>
                      </div>
                      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: "100%", height: 160, display: "block" }}>
                        <defs>
                          <linearGradient id="gaArea" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#ff6a00" stopOpacity="0.40" />
                            <stop offset="100%" stopColor="#ff6a00" stopOpacity="0" />
                          </linearGradient>
                        </defs>
                        <polygon points={area} fill="url(#gaArea)" />
                        <polyline points={line} fill="none" stroke="#ff6a00" strokeWidth="2" vectorEffect="non-scaling-stroke" />
                        {data.map((d, i) => (
                          <circle key={d.date} cx={xAt(i)} cy={yAt(d.activeUsers)} r="3.5" fill="#ff6a00" vectorEffect="non-scaling-stroke">
                            <title>{`${lbl(d.date)}: ${d.activeUsers} visitors · ${d.sessions} sessions`}</title>
                          </circle>
                        ))}
                      </svg>
                      <div className="muted" style={{ display: "flex", justifyContent: "space-between", marginTop: 4, fontSize: 12 }}>
                        <span>{lbl(data[0].date)}</span>
                        {n > 2 && <span>{lbl(data[Math.floor((n - 1) / 2)].date)}</span>}
                        <span>{lbl(data[n - 1].date)}</span>
                      </div>
                    </div>
                  );
                })()}
                {ga.byPlatform?.length > 0 && (
                  <div style={{ marginTop: 18 }}>
                    <div className="muted" style={{ marginBottom: 8 }}>Traffic by platform</div>
                    {ga.byPlatform.map((p) => (
                      <div
                        key={p.platform}
                        style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid rgba(255,255,255,0.06)" }}
                      >
                        <span>{p.platform}</span>
                        <span className="muted">
                          {p.users.toLocaleString()} visitors · {p.sessions.toLocaleString()} sessions
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="admindash-empty">
                Google Analytics isn't connected yet. Set <code>GA4_PROPERTY_ID</code> and{" "}
                <code>GA_SERVICE_ACCOUNT_JSON</code> on the API service and redeploy to see live visitor data here.
              </div>
            )}
          </div>

          {/* Verify TikTok Events API (server-side conversions) */}
          <div className="admindash-section admindash-fade-in" style={show('health')}>
            <h2>TikTok Events API</h2>
            <div className="muted" style={{ marginBottom: 12 }}>
              Fire a server-side <b>CompletePayment</b> test event to confirm the Events API token works —
              no real charge. Paste a <b>Test Event Code</b> from TikTok Events Manager → Test Events so it
              lands in the debugger (and isn't counted as live data). Leave blank to send a live event.
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              <input
                type="text"
                value={ttTestCode}
                onChange={(e) => setTtTestCode(e.target.value)}
                placeholder="TikTok test_event_code (optional)"
                style={{
                  flex: "1 1 280px", padding: "10px 12px", borderRadius: 8,
                  border: "1px solid rgba(255,255,255,0.15)", background: "rgba(255,255,255,0.04)", color: "#fff",
                }}
              />
              <button className="admindash-refresh" onClick={sendTikTokTest} disabled={ttTesting}>
                {ttTesting ? "Sending…" : "Send test event"}
              </button>
            </div>
          </div>

          {/* Two charts side-by-side — signups + clips per day */}
          <div className="admindash-two-col" style={show('overview')}>
            <div className="admindash-section admindash-fade-in">
              <h2>Signups — last 7 days</h2>
              <div className="admindash-chart">
                {weeklySignups.map((d) => {
                  const heightPct = (d.count / maxSignup) * 100;
                  return (
                    <div key={d.date} className="admindash-chart-col" title={`${d.date}: ${d.count} signups`}>
                      <div className="admindash-chart-bar" style={{ height: `${Math.max(2, heightPct)}%` }}>
                        <span className="admindash-chart-count">{d.count}</span>
                      </div>
                      <span className="admindash-chart-label">{dayLabel(d.date)}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="admindash-section admindash-fade-in">
              <h2>Clips generated — last 7 days</h2>
              <div className="admindash-chart">
                {(weeklyClips || []).map((d) => {
                  const heightPct = (d.clips / maxClips) * 100;
                  return (
                    <div key={d.date} className="admindash-chart-col" title={`${d.date}: ${d.clips} clips`}>
                      <div className="admindash-chart-bar admindash-chart-bar--orange" style={{ height: `${Math.max(2, heightPct)}%` }}>
                        <span className="admindash-chart-count">{d.clips}</span>
                      </div>
                      <span className="admindash-chart-label">{dayLabel(d.date)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Traffic sources — proxy for "where users come from" */}
          {referralSources && referralSources.length > 0 && (
            <div className="admindash-section admindash-fade-in" style={show('analytics')}>
              <h2>Top traffic sources</h2>
              <div className="admindash-sources">
                {referralSources.map((s) => {
                  const max = Math.max(1, ...referralSources.map((x) => x.count));
                  const pct = (s.count / max) * 100;
                  return (
                    <div key={s.source} className="admindash-source-row">
                      <span className="admindash-source-label">{s.source}</span>
                      <div className="admindash-source-bar-track">
                        <div className="admindash-source-bar-fill" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="admindash-source-count">{s.count}</span>
                    </div>
                  );
                })}
              </div>
              <div className="admindash-note">
                Tracked via the <code>referralSource</code> field set at onboarding. Full page-view analytics (GA / Plausible) is a separate integration.
              </div>
            </div>
          )}

          {/* How users found us — self-reported attribution from onboarding */}
          {foundVia && foundVia.length > 0 && (() => {
            const LABELS = { twitter: "X / Twitter", youtube: "YouTube", tiktok: "TikTok", instagram: "Instagram", google: "Google Search", friend: "Friend / Word of mouth", other: "Other" };
            const max = Math.max(1, ...foundVia.map((x) => x.count));
            const total = foundVia.reduce((a, x) => a + x.count, 0);
            return (
              <div className="admindash-section admindash-fade-in" style={show('analytics')}>
                <h2>How users found us</h2>
                <div className="admindash-sources">
                  {foundVia.map((s) => {
                    const pct = (s.count / max) * 100;
                    const share = total ? Math.round((s.count / total) * 100) : 0;
                    return (
                      <div key={s.source} className="admindash-source-row">
                        <span className="admindash-source-label">{LABELS[s.source] || s.source}</span>
                        <div className="admindash-source-bar-track">
                          <div className="admindash-source-bar-fill" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="admindash-source-count">{s.count} · {share}%</span>
                      </div>
                    );
                  })}
                </div>
                <div className="admindash-note">
                  Self-reported at onboarding ("How did you find Clipbait?"). {total} answered.
                </div>
              </div>
            );
          })()}

          {tab === "users" && <AdminUsersList />}
          <div className="admindash-two-col" style={show('users')}>
            <div className="admindash-section admindash-fade-in">
              <h2>Recently active</h2>
              {recentlyActive.length === 0 ? (
                <div className="admindash-empty-sm">No activity data yet.</div>
              ) : (
                <table className="admindash-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Type</th>
                      <th>Plan</th>
                      <th>Location</th>
                      <th>Last active</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentlyActive.map((u) => (
                      <tr
                        key={u._id}
                        className="admindash-row-clickable"
                        onClick={() => navigate(`/admin/users/${u._id}`)}
                      >
                        <td><UserCell u={u} /></td>
                        <td>
                          {u.onboarding?.userType ? (
                            <span className="type-chip-wrap">
                              <span className="type-chip">{u.onboarding.userType}</span>
                              {u.onboarding.userType === "clipper" && u.onboarding.wantsOpportunities && (
                                <span className="paid-clipper-chip" title="Wants paid clipping opportunities">$</span>
                              )}
                            </span>
                          ) : (
                            <span className="muted">—</span>
                          )}
                        </td>
                        <td>{u.plan && u.plan !== "none" ? <span className="plan-chip">{u.plan}</span> : <span className="muted">free</span>}</td>
                        <td><LocationCell geo={u.geo} /></td>
                        <td>{formatRel(u.lastActiveAt || u.lastSignInAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="admindash-section admindash-fade-in">
              <h2>Recent signups</h2>
              <table className="admindash-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Type</th>
                    <th>Plan</th>
                    <th>Location</th>
                    <th>Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {recentSignups.map((u) => (
                    <tr
                      key={u._id}
                      className="admindash-row-clickable"
                      onClick={() => navigate(`/admin/users/${u._id}`)}
                      title="Click to view details"
                    >
                      <td><UserCell u={u} /></td>
                      <td>{u.onboarding?.userType ? <span className="type-chip">{u.onboarding.userType}</span> : <span className="muted">—</span>}</td>
                      <td>{u.plan && u.plan !== "none" ? <span className="plan-chip">{u.plan}</span> : <span className="muted">free</span>}</td>
                      <td><LocationCell geo={u.geo} /></td>
                      <td>{formatRel(u.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* FAILED — durable failure log (web + Modal worker) */}
          {tab === "failed" && <FailedTab />}

          {/* ONBOARDING — agency intake form submissions */}
          {tab === "onboarding" && <OnboardingTab />}

          {/* DISTRIBUTION — who connected accounts + who posted clips */}
          {tab === "distribution" && <DistributionTab />}

          {/* AFFILIATES — affiliate portal admin view (recruits + their boards) */}
          {tab === "affiliates" && <AffiliatesTab />}
          {tab === "clippers" && <ClippersTab />}
          {tab === "clip-review" && <ClipReviewTab />}
          {tab === "clip-quality" && <ClipQualityTab />}
          {tab === "pnl" && <FinanceTab />}
          {tab === "sms" && <SmsTab />}
          {tab === "instagram" && <IgContentTab />}
          {tab === "content-engine" && <ContentEngineTab />}


          <div className="admindash-foot">
            Generated {formatRel(data.generatedAt)}
          </div>
        </div>
      </div>
    </>
  );
}

function FailedTab() {
  const [state, setState] = useState({ loading: true, items: [], total: 0, unresolved: 0, bySource: {}, byCategory: [] });
  const [renderLogs, setRenderLogs] = useState(null); // { configured, logs, error }
  const [filter, setFilter] = useState({ source: "", category: "", resolved: "false" });

  const load = async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const params = new URLSearchParams();
      if (filter.source) params.set("source", filter.source);
      if (filter.category) params.set("category", filter.category);
      if (filter.resolved) params.set("resolved", filter.resolved);
      const res = await axiosInstance.get(`/admin/failures?${params}`);
      setState({ loading: false, ...res.data });
    } catch (err) {
      setState((s) => ({ ...s, loading: false }));
      toast.error(err.response?.data?.error || "Failed to load failures");
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter.source, filter.category, filter.resolved]);

  const loadRenderLogs = async () => {
    try {
      const res = await axiosInstance.get("/admin/failures/render-logs");
      setRenderLogs(res.data);
      if (res.data?.configured === false) toast("Render logs not configured (set RENDER_API_KEY).");
    } catch { toast.error("Couldn't fetch Render logs"); }
  };

  const resolve = async (id) => {
    try { await axiosInstance.post(`/admin/failures/${id}/resolve`, { resolved: true }); load(); }
    catch { toast.error("Couldn't resolve"); }
  };
  const clearResolved = async () => {
    if (!window.confirm("Delete all resolved failures?")) return;
    try { const r = await axiosInstance.post("/admin/failures/clear", {}); toast.success(`Cleared ${r.data.deleted}`); load(); }
    catch { toast.error("Couldn't clear"); }
  };

  const SOURCE_COLORS = { web: "#60a5fa", worker: "#f59e0b", modal: "#a78bfa", render: "#34d399", other: "#a1a1aa" };

  return (
    <div className="admindash-section admindash-fade-in">
      <div className="admindash-failed-head">
        <div>
          <h2 className="admindash-section-title" style={{ margin: 0 }}>Failures</h2>
          <div className="muted">{state.unresolved} unresolved · {state.total} total (auto-expire after 30 days)</div>
        </div>
        <div className="admindash-failed-actions">
          <button className="admindash-refresh" onClick={load}>Refresh</button>
          <button className="admindash-refresh" onClick={loadRenderLogs}>Pull Render logs</button>
          <button className="admindash-refresh" onClick={clearResolved}>Clear resolved</button>
        </div>
      </div>

      {/* summary chips by category */}
      {state.byCategory?.length > 0 && (
        <div className="admindash-failed-chips">
          {state.byCategory.map((c) => (
            <span key={c.category} className="admindash-failed-chip">{c.category}<b>{c.count}</b></span>
          ))}
        </div>
      )}

      {/* filters */}
      <div className="admindash-failed-filters">
        <select value={filter.source} onChange={(e) => setFilter((f) => ({ ...f, source: e.target.value }))}>
          <option value="">All sources</option>
          <option value="web">Web (Render)</option>
          <option value="worker">Worker</option>
          <option value="modal">Modal</option>
        </select>
        <select value={filter.category} onChange={(e) => setFilter((f) => ({ ...f, category: e.target.value }))}>
          <option value="">All categories</option>
          <option value="social-post">social-post</option>
          <option value="live-session">live-session</option>
          <option value="clip-job">clip-job</option>
          <option value="worker">worker</option>
        </select>
        <select value={filter.resolved} onChange={(e) => setFilter((f) => ({ ...f, resolved: e.target.value }))}>
          <option value="false">Unresolved</option>
          <option value="true">Resolved</option>
          <option value="">All</option>
        </select>
      </div>

      {state.loading ? (
        <div className="admindash-empty admindash-shimmer">Loading…</div>
      ) : state.items.length === 0 ? (
        <div className="admindash-empty"><FiCheckCircle /> No failures{filter.resolved === "false" ? " outstanding" : ""}.</div>
      ) : (
        <div className="admindash-failed-list">
          {state.items.map((it) => (
            <div key={it._id} className={`admindash-failed-row ${it.resolved ? "resolved" : ""}`}>
              <span className="admindash-failed-src" style={{ background: SOURCE_COLORS[it.source] || "#a1a1aa" }}>{it.source}</span>
              <div className="admindash-failed-main">
                <div className="admindash-failed-msg">{it.message}</div>
                <div className="admindash-failed-meta">
                  <span className="admindash-failed-cat">{it.category}</span>
                  {it.context?.platform && <span>· {it.context.platform}</span>}
                  {it.context?.userId && <span>· user {String(it.context.userId).slice(-6)}</span>}
                  {it.context?.statusCode && <span>· HTTP {it.context.statusCode}</span>}
                  <span>· {formatRel(it.createdAt)}</span>
                </div>
                {it.detail && <details className="admindash-failed-detail"><summary>detail</summary><pre>{it.detail}</pre></details>}
              </div>
              {!it.resolved && <button className="admindash-failed-resolve" onClick={() => resolve(it._id)}>Resolve</button>}
            </div>
          ))}
        </div>
      )}

      {renderLogs && (
        <div className="admindash-failed-render">
          <h3 className="admindash-section-title">Render logs (recent errors)</h3>
          {renderLogs.configured === false ? (
            <div className="muted">Not configured. Set <code>RENDER_API_KEY</code>, <code>RENDER_OWNER_ID</code>, <code>RENDER_SERVICE_ID</code> to pull live Render logs here.</div>
          ) : renderLogs.error ? (
            <div className="error-text">{renderLogs.error}</div>
          ) : renderLogs.logs?.length === 0 ? (
            <div className="muted">No recent error logs.</div>
          ) : (
            <pre className="admindash-failed-renderpre">{renderLogs.logs.map((l) => `${l.timestamp || ""}  ${l.message}`).join("\n")}</pre>
          )}
        </div>
      )}
    </div>
  );
}

const ONB_QUESTIONS = [
  { key: "goal", label: "Primary goal" },
  { key: "promote", label: "Wants pushed / promoted now" },
  { key: "trafficPages", label: "Main pages to direct traffic to" },
  { key: "contentStyles", label: "Preferred content styles" },
  { key: "styleRef", label: "Style reference (account/clips)" },
  { key: "postingTargets", label: "Where to post" },
  { key: "nicheRefs", label: "Niche brands/figures to research" },
  { key: "assets", label: "Content assets available" },
];
const POST_TARGET_LABELS = {
  A: "Own brand pages only", B: "Theme pages only", C: "Both (branded + theme)",
};

function OnboardingTab() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    axiosInstance.get("/agency-onboarding")
      .then((r) => setItems(r.data.items || []))
      .catch((e) => { setItems([]); toast.error(e.response?.data?.error || "Failed to load onboarding"); });
  }, []);

  if (!items) return <div className="admindash-section admindash-fade-in"><div className="admindash-empty admindash-shimmer">Loading…</div></div>;

  return (
    <div className="admindash-section admindash-fade-in">
      <h2 className="admindash-section-title">Agency onboarding <span className="muted">({items.length})</span></h2>
      {items.length === 0 ? (
        <div className="admindash-empty">No submissions yet. Share <code>/agency-onboarding</code> with clients.</div>
      ) : (
        <div className="admindash-onb-list">
          {items.map((it) => (
            <div key={it._id} className="admindash-onb-card">
              <div className="admindash-onb-head">
                <div>
                  <div className="admindash-onb-name">{[it.firstName, it.lastName].filter(Boolean).join(" ") || "—"}</div>
                  {it.company && <div className="admindash-onb-company">{it.company}</div>}
                </div>
                <span className="muted">{formatRel(it.createdAt)}</span>
              </div>
              <div className="admindash-onb-qa">
                {ONB_QUESTIONS.map((q) => {
                  let val = it[q.key];
                  if (q.key === "postingTargets") {
                    val = (val || []).map((t) => POST_TARGET_LABELS[t] || t).join(", ");
                  }
                  if (!val) return null;
                  return (
                    <div key={q.key} className="admindash-onb-item">
                      <div className="admindash-onb-q">{q.label}</div>
                      <div className="admindash-onb-a">{val}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PlatformBadge({ platform, status }) {
  const colors = { x: "#e7e7e7", twitch: "#9146FF", kick: "#53FC18", youtube: "#FF0000", tiktok: "#69C9D0", facebook: "#1877F2" };
  const style = { background: colors[platform] || "#a1a1aa", color: platform === "x" ? "#000" : "#0a0a0a" };
  return <span className={`admindash-pbadge ${status ? `st-${status}` : ""}`} style={style}>{platform}</span>;
}

function DistributionTab() {
  const [conns, setConns] = useState(null);
  const [posts, setPosts] = useState(null);
  useEffect(() => {
    axiosInstance.get("/admin/connections").then((r) => setConns(r.data)).catch(() => setConns({ items: [] }));
    axiosInstance.get("/admin/posts?status=posted").then((r) => setPosts(r.data)).catch(() => setPosts({ items: [] }));
  }, []);

  return (
    <>
      {/* Connected accounts */}
      <div className="admindash-section admindash-fade-in">
        <h2 className="admindash-section-title">Connected accounts <span className="muted">({conns?.total ?? "…"})</span></h2>
        {!conns ? <div className="admindash-empty admindash-shimmer">Loading…</div>
          : conns.items.length === 0 ? <div className="admindash-empty">No connected accounts yet.</div>
          : (
            <div className="admindash-conn-list">
              {conns.items.map((u) => (
                <div key={u.userId} className="admindash-conn-row">
                  <div className="admindash-conn-user">
                    <div className="admindash-user-name">{u.username}</div>
                    <div className="admindash-user-email">{u.email}</div>
                  </div>
                  <div className="admindash-conn-platforms">
                    {u.platforms.map((p) => (
                      <span key={p.platform} className="admindash-conn-pill" title={p.username ? `@${p.username}` : ""}>
                        <PlatformBadge platform={p.platform} />
                        {p.username ? `@${p.username}` : p.platform}
                        {p.autoClip && <span className="admindash-conn-auto">auto-clip</span>}
                      </span>
                    ))}
                  </div>
                  <span className="muted admindash-conn-when">{u.lastConnectedAt ? formatRel(u.lastConnectedAt) : ""}</span>
                </div>
              ))}
            </div>
          )}
      </div>

      {/* Posted clips */}
      <div className="admindash-section admindash-fade-in">
        <h2 className="admindash-section-title">Posted clips <span className="muted">({posts?.postedCount ?? "…"})</span></h2>
        {!posts ? <div className="admindash-empty admindash-shimmer">Loading…</div>
          : posts.items.length === 0 ? <div className="admindash-empty">No clips posted yet.</div>
          : (
            <div className="admindash-posts-list">
              {posts.items.map((p) => (
                <div key={p.id} className="admindash-post-row">
                  <div className="admindash-post-main">
                    <div className="admindash-post-user">{p.user?.username || "—"} <span className="muted">· {p.user?.email}</span></div>
                    {p.caption && <div className="admindash-post-caption">{p.caption}</div>}
                    <div className="admindash-post-platforms">
                      {p.platforms.map((pl, i) => (
                        pl.postUrl ? (
                          <a key={i} href={pl.postUrl} target="_blank" rel="noreferrer" className="admindash-post-link">
                            <PlatformBadge platform={pl.platform} status={pl.status} /> view →
                          </a>
                        ) : (
                          <span key={i} className="admindash-post-link"><PlatformBadge platform={pl.platform} status={pl.status} /> {pl.status}</span>
                        )
                      ))}
                    </div>
                  </div>
                  <span className="muted">{formatRel(p.updatedAt)}</span>
                </div>
              ))}
            </div>
          )}
      </div>
    </>
  );
}

function Stat({ label, value, accent, pulse, icon }) {
  return (
    <div className={`admindash-stat ${accent ? `admindash-stat--${accent}` : ""} ${pulse ? "admindash-stat--pulse" : ""}`}>
      {icon && <div className="admindash-stat-icon">{icon}</div>}
      <div className="admindash-stat-value">{value}</div>
      <div className="admindash-stat-label">{label}</div>
    </div>
  );
}

// "Active" = lastActiveAt within the last 5 minutes
function isActive(lastActiveAt) {
  if (!lastActiveAt) return false;
  return Date.now() - new Date(lastActiveAt).getTime() < 5 * 60 * 1000;
}

function UserCell({ u }) {
  const [imgError, setImgError] = useState(false);
  const showImg = u.profilePicture && !imgError;
  const active = isActive(u.lastActiveAt);
  return (
    <div className="admindash-user">
      <div className="admindash-user-avatar-wrap">
        {showImg ? (
          <img
            src={u.profilePicture}
            alt=""
            className="admindash-user-avatar"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="admindash-user-avatar admindash-user-avatar--blank" />
        )}
        <span
          className={`admindash-user-status ${active ? "online" : "offline"}`}
          title={active ? "Active now" : "Offline"}
        />
      </div>
      <div className="admindash-user-meta">
        <div className="admindash-user-name">{u.username || "—"}</div>
        <div className="admindash-user-email">{u.email}</div>
      </div>
    </div>
  );
}

function HealthCard({ name, status, detail, configMissingMsg, dashboardUrl }) {
  let badge;
  if (status.manual) badge = <span className="health-badge">Manual check</span>;
  else if (!status.configured) badge = <span className="health-badge health-badge--off">Not configured</span>;
  else if (status.ok) badge = <span className="health-badge health-badge--ok">Live</span>;
  else if (status.error) badge = <span className="health-badge health-badge--err">Error</span>;
  else badge = <span className="health-badge">Manual check</span>;

  return (
    <div className="admindash-health-card">
      <div className="admindash-health-head">
        <span className="admindash-health-name">{name}</span>
        {badge}
      </div>
      <div className="admindash-health-body">
        {!status.manual && !status.configured ? (
          <div className="muted">{configMissingMsg}</div>
        ) : status.error ? (
          <div className="error-text">{status.error}</div>
        ) : (
          detail
        )}
      </div>
      {dashboardUrl && (
        <a className="admindash-health-link" href={dashboardUrl} target="_blank" rel="noreferrer">
          Open dashboard →
        </a>
      )}
    </div>
  );
}
